import React, { useState, useRef } from 'react';
import {
  X, Search, Loader2, Users, Send, UserMinus, AlertTriangle,
  CheckCircle, ChevronDown, ChevronUp, Calendar, List, Tag, Info,
} from 'lucide-react';
import { interspireApi } from '../services/api';
import { EmailSendRow, UnifiedDomainResult, DomainContactSample } from '../types';

interface Props {
  row: EmailSendRow;
  onClose: () => void;
}

function formatUnixDate(ts: number | null): string {
  if (ts == null || ts === 0) return '—';
  return new Date(ts * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

type Phase = 'idle' | 'analyzing' | 'result' | 'error';

export default function DomainInvestigator({ row, onClose }: Props) {
  const [domain, setDomain]           = useState('');
  const [phase, setPhase]             = useState<Phase>('idle');
  const [result, setResult]           = useState<UnifiedDomainResult | null>(null);
  const [errorMsg, setErrorMsg]       = useState('');
  const [showSamples, setShowSamples] = useState(false);
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [samples, setSamples]         = useState<DomainContactSample[]>([]);
  const [showExcluded, setShowExcluded]         = useState(false);
  const [showMissingLists, setShowMissingLists] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAnalyze = async () => {
    const d = domain.trim().toLowerCase().replace(/^@/, '');
    if (!d) { inputRef.current?.focus(); return; }
    setPhase('analyzing');
    setResult(null);
    setShowSamples(false);
    setSamples([]);
    setErrorMsg('');
    setShowExcluded(false);
    setShowMissingLists(false);
    try {
      const res = await interspireApi.investigateDomain(row.statid, d, false, row.segmentId);
      setResult(res.data);
      setPhase('result');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error ?? err?.message ?? 'Analysis failed');
      setPhase('error');
    }
  };

  const handleLoadSamples = async () => {
    const d = domain.trim().toLowerCase().replace(/^@/, '');
    setSamplesLoading(true);
    try {
      const res = await interspireApi.investigateDomain(row.statid, d, true, row.segmentId);
      setSamples(res.data.sampleContacts ?? []);
      setShowSamples(true);
    } catch {
      setSamples([]);
      setShowSamples(true);
    } finally {
      setSamplesLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAnalyze();
  };

  const db  = result?.db;
  const seg = result?.segment;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Domain Investigation</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Send details (auto-populated) */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2 text-sm">
            <h3 className="font-semibold text-blue-900 mb-3">Send Details</h3>
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              <span className="text-blue-600 flex items-center gap-1.5 whitespace-nowrap">
                <List className="h-3.5 w-3.5" /> Campaign
              </span>
              <span className="font-medium text-gray-900 truncate" title={row.campaignName}>
                {row.campaignName || '—'}
              </span>

              <span className="text-blue-600 flex items-center gap-1.5 whitespace-nowrap">
                <Tag className="h-3.5 w-3.5" /> Send ID
              </span>
              <span className="font-mono text-gray-700">{row.statid}</span>

              <span className="text-blue-600 flex items-center gap-1.5 whitespace-nowrap">
                <Users className="h-3.5 w-3.5" /> Segment
              </span>
              <span className="text-gray-700">{row.segmentName ?? '—'}</span>

              <span className="text-blue-600 flex items-center gap-1.5 whitespace-nowrap">
                <Calendar className="h-3.5 w-3.5" /> Date
              </span>
              <span className="text-gray-500">{formatUnixDate(row.startDate)}</span>

              <span className="text-blue-600 flex items-center gap-1.5 whitespace-nowrap">
                <Send className="h-3.5 w-3.5" /> Recipients
              </span>
              <span className="font-mono text-gray-700">{row.recipientsCount.toLocaleString()}</span>
            </div>
          </div>

          {/* Domain input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Domain to investigate
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm select-none">@</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={domain}
                  onChange={e => setDomain(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="example.gov"
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={phase === 'analyzing'}
                  autoFocus
                />
              </div>
              <button
                onClick={handleAnalyze}
                disabled={phase === 'analyzing' || !domain.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {phase === 'analyzing'
                  ? <><Loader2 className="h-4 w-4 animate-spin" />Analyzing…</>
                  : <><Search className="h-4 w-4" />Analyze Domain</>
                }
              </button>
            </div>
          </div>

          {/* Error */}
          {phase === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              {errorMsg}
            </div>
          )}

          {/* Results */}
          {phase === 'result' && result && db && (
            <>
              {/* ── Section 1: Whole-database counts ── */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Entire Database — @{domain.replace(/^@/, '')}
                </h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className="px-4 py-2.5 flex items-center gap-2 text-gray-700">
                          <Users className="h-4 w-4 text-gray-400" /> Total contacts
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-gray-900">
                          {db.total.toLocaleString()}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2.5">
                          <span className="flex items-center gap-2 text-amber-700">
                            <UserMinus className="h-4 w-4" /> Unsubscribed
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-amber-700">
                          {db.unsubscribed.toLocaleString()}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2.5">
                          <span className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="h-4 w-4" /> Bounced
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-red-700">
                          {db.bounced.toLocaleString()}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2.5">
                          <span className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="h-4 w-4" /> Active
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-green-700">
                          {db.active.toLocaleString()}
                        </td>
                      </tr>
                      {result.sent != null && (
                        <tr className="bg-blue-50">
                          <td className="px-4 py-2.5">
                            <span className="flex items-center gap-2 text-blue-700 font-medium">
                              <Send className="h-4 w-4" /> Sent (this campaign)
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-blue-700">
                            {result.sent.toLocaleString()}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Section 2: Segment-scoped counts ── */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Segment Analysis
                </h3>
                {seg ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-purple-50 border-b border-gray-200 text-xs text-purple-700 font-medium">
                      Segment: {seg.segmentName}
                    </div>
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Count</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr>
                          <td className="px-4 py-2.5 flex items-center gap-2 text-gray-700">
                            <Users className="h-4 w-4 text-gray-400" /> In segment's lists
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-gray-900">
                            {seg.listTotal.toLocaleString()}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2.5">
                            <span className="flex items-center gap-2 text-amber-700">
                              <UserMinus className="h-4 w-4" /> Unsubscribed
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-amber-700">
                            {seg.listUnsubscribed.toLocaleString()}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2.5">
                            <span className="flex items-center gap-2 text-red-700">
                              <AlertTriangle className="h-4 w-4" /> Bounced
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-red-700">
                            {seg.listBounced.toLocaleString()}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2.5">
                            <span className="flex items-center gap-2 text-green-700">
                              <CheckCircle className="h-4 w-4" /> Active in lists
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-green-700">
                            {seg.listActive.toLocaleString()}
                          </td>
                        </tr>
                        <tr className="bg-green-50">
                          <td className="px-4 py-2.5">
                            <span className="flex items-center gap-2 text-green-800 font-medium">
                              <CheckCircle className="h-4 w-4" /> In segment (matches rules)
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-green-800">
                            {seg.inSegment.toLocaleString()}
                          </td>
                        </tr>
                        <tr className="bg-yellow-50">
                          <td className="px-4 py-2.5">
                            <span className="flex items-center gap-2 text-yellow-800 font-medium">
                              <Info className="h-4 w-4" /> Eligible but not in segment
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold text-yellow-800">
                            {seg.notInSegment.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-500">
                    <Info className="h-4 w-4 flex-shrink-0" />
                    This send did not use a Segment — no segment-level breakdown available.
                  </div>
                )}
              </div>

              {/* ── Group A: Excluded by rules ── */}
              {result.excludedByRules && result.excludedByRules.length > 0 && (
                <div className="border border-orange-200 dark:border-orange-700/50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowExcluded(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/25 dark:hover:bg-orange-900/40 transition-colors text-left"
                  >
                    <span className="text-sm font-semibold text-orange-800 dark:text-orange-300">
                      {result.excludedByRules.length} active contact{result.excludedByRules.length !== 1 ? 's' : ''} excluded by segment rules — click to see why
                    </span>
                    <ChevronDown className={`h-4 w-4 text-orange-600 dark:text-orange-400 transition-transform ${showExcluded ? 'rotate-180' : ''}`} />
                  </button>
                  {showExcluded && (
                    <div className="p-4 space-y-3">
                      {result.ruleFields && result.ruleFields.length > 0 && (
                        <div className="bg-orange-50 border border-orange-200 rounded px-3 py-2 text-xs text-orange-800">
                          <span className="font-semibold">Segment requires: </span>
                          {result.ruleFields.map(rf => (
                            <span key={rf.fieldId} className="mr-3">
                              <span className="font-medium">{rf.fieldName}:</span> {rf.allowedValues.join(', ')}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-500">Email</th>
                              {result.ruleFields?.map(rf => (
                                <th key={rf.fieldId} className="px-3 py-2 text-left font-semibold text-gray-500">{rf.fieldName}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {result.excludedByRules.map((c, i) => (
                              <tr key={i} className="bg-white hover:bg-gray-50">
                                <td className="px-3 py-1.5 font-mono text-gray-700">{c.email}</td>
                                {c.fieldValues.map(fv => (
                                  <td key={fv.fieldId} className="px-3 py-1.5">
                                    {fv.value
                                      ? <span className="text-red-700 font-medium">{fv.value}</span>
                                      : <span className="text-gray-300 italic">no value</span>
                                    }
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Group B: In other lists ── */}
              {result.missingByList && result.missingByList.length > 0 && (
                <div className="border border-blue-200 dark:border-blue-700/50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowMissingLists(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/25 dark:hover:bg-blue-900/40 transition-colors text-left"
                  >
                    <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                      {result.missingByList.reduce((s, r) => s + r.count, 0).toLocaleString()} contacts in other lists (not this segment's lists) — click to see which
                    </span>
                    <ChevronDown className={`h-4 w-4 text-blue-600 dark:text-blue-400 transition-transform ${showMissingLists ? 'rotate-180' : ''}`} />
                  </button>
                  {showMissingLists && (
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold text-gray-500">List Name</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-500">Total</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-500">Active</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-500">Unsub</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-500">Bounced</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.missingByList.map(r => (
                          <tr key={r.listId} className="bg-white hover:bg-gray-50">
                            <td className="px-4 py-1.5 text-gray-700">{r.listName}</td>
                            <td className="px-4 py-1.5 text-right font-semibold text-gray-900">{r.count.toLocaleString()}</td>
                            <td className="px-4 py-1.5 text-right text-green-700">{r.active.toLocaleString()}</td>
                            <td className="px-4 py-1.5 text-right text-amber-700">{r.unsubscribed.toLocaleString()}</td>
                            <td className="px-4 py-1.5 text-right text-red-700">{r.bounced.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Auto-generated explanation */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Conclusion</h3>
                <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {result.explanation}
                </p>
              </div>

              {/* Sample contacts */}
              <div>
                {!showSamples ? (
                  <button
                    onClick={handleLoadSamples}
                    disabled={samplesLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {samplesLoading
                      ? <><Loader2 className="h-4 w-4 animate-spin" />Loading samples…</>
                      : <><Users className="h-4 w-4" />Show Sample Contacts</>
                    }
                  </button>
                ) : (
                  <div>
                    <button
                      onClick={() => setShowSamples(false)}
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
                    >
                      {showSamples ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      Hide Sample Contacts
                    </button>
                    {samples.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">No contacts found for this domain.</p>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Unsub</th>
                              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Bounced</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {samples.map((s, i) => (
                              <tr key={i} className="bg-white hover:bg-gray-50">
                                <td className="px-4 py-2 font-mono text-xs text-gray-700">{s.emailaddress}</td>
                                <td className="px-4 py-2 text-center">
                                  {s.unsubscribed > 0
                                    ? <span className="text-amber-600 font-semibold">Yes</span>
                                    : <span className="text-gray-300">—</span>
                                  }
                                </td>
                                <td className="px-4 py-2 text-center">
                                  {s.bounced > 0
                                    ? <span className="text-red-600 font-semibold">Yes</span>
                                    : <span className="text-gray-300">—</span>
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
                          Showing up to 25 contacts
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
